import {
  CurrentIntakeQuestion,
  PatientScreeningQuestionResponse,
} from "~/common/utils";

export const PATIENT_PREVIEW_CONSULTATION_TYPES = {
  screening: "screening",
  intake: "intake",
};

export type ResponseQuestionTypes =
  (typeof PATIENT_PREVIEW_CONSULTATION_TYPES)[keyof typeof PATIENT_PREVIEW_CONSULTATION_TYPES];

export const SUMMARY_TYPE = {
  intake: "intake",
  screening: "screening",
  treatment_recommendations: "treatment_recommendations",
} as const;

export type SummaryType = (typeof SUMMARY_TYPE)[keyof typeof SUMMARY_TYPE];

export const POTENTIAL_AREAS_OF_CONCERN_SUGGESTIONS = {
  direct_report_skew: "Direct report skew",
  personality_pathology: "Personality pathology",
  impulsivity: "Impulsivity",
  eating_disorder: "Eating disorder",
  substance_use: "Substance use",
} as const;

export type PotentialAreasOfConcernSuggestionsType =
  (typeof POTENTIAL_AREAS_OF_CONCERN_SUGGESTIONS)[keyof typeof POTENTIAL_AREAS_OF_CONCERN_SUGGESTIONS];

export interface PotentialAreasOfConcern {
  flag: string;
  suggestions: PotentialAreasOfConcernSuggestionsType[];
}

export interface ScoreData {
  score: number;
  label: string;
}

export interface ScoresData {
  asrs: ScoreData;
  gad7: ScoreData;
  phq9: ScoreData;
}

export interface MedicationsData {
  name: string;
  dose: string;
  frequency: string;
}

interface ClinicalDimensionsData {
  potential_areas_of_concern: string[];
  no_signs_of_concern: string[];
}

export interface ResponseTheme {
  condition_type: string;
  condition_value: string;
  id: string;
  flow_node: PatientScreeningQuestionResponse | CurrentIntakeQuestion;
  question_theme: {
    code: string;
    id: string;
    name: string;
    order: number;
  };
  triggered: boolean;
}

export interface ScreeningSummaryDataV1 {
  id: string;
  short_summary: string;
  long_summary: string;
  scores: ScoresData;
  significant_clinical_dimensions: ClinicalDimensionsData;
  medications?: MedicationsData[];
}

export interface ScreeningSummaryDataV2 {
  id: string;
  case_summary: string;
  potential_areas_of_concern: PotentialAreasOfConcern[];
  scores: ScoresData;
  medications?: MedicationsData[];
}

export interface SummaryFeedback {
  consultation_summary_id: string;
  consultation_type: SummaryType;
  id: string;
  is_like: boolean;
  team_member_id: string;
}

export interface PatientScreeningConsultationSummaryV1 {
  id: string;
  feedbacks: SummaryFeedback[];
  consultation_id: string;
  type: ResponseQuestionTypes;
  data: ScreeningSummaryDataV1;
  version: 1;
}

export interface PatientScreeningConsultationSummaryV2 {
  id: string;
  feedbacks: SummaryFeedback[];
  consultation_id: string;
  type: ResponseQuestionTypes;
  data: ScreeningSummaryDataV2;
  version: 2;
}

type ScreeningSummaryDataV3 = ScreeningSummaryDataV2;

export interface PatientScreeningConsultationSummaryV3 {
  id: string;
  feedbacks: SummaryFeedback[];
  consultation_id: string;
  type: ResponseQuestionTypes;
  data: ScreeningSummaryDataV3;
  version: 3;
}

type ScreeningSummaryDataV4 = ScreeningSummaryDataV3;

export interface PatientScreeningConsultationSummaryV4 {
  id: string;
  feedbacks: SummaryFeedback[];
  consultation_id: string;
  type: ResponseQuestionTypes;
  data: ScreeningSummaryDataV4;
  version: 4;
  response_themes: ResponseTheme[];
}

export type PatientScreeningConsultationSummary =
  | PatientScreeningConsultationSummaryV1
  | PatientScreeningConsultationSummaryV2
  | PatientScreeningConsultationSummaryV3
  | PatientScreeningConsultationSummaryV4;
